import moment from "moment";

/**
 * The status an admin actually sees on a booking.
 *
 * `bookingStatus` alone is not the answer: a booking is only Confirmed once the
 * money is in, so the payment status decides between Confirmed and Pending.
 * Booking detail (ManageBookingV2) and the booking calendar both read this, so
 * one booking never shows two different statuses on two screens.
 *
 * Returns "CANCELLED" | "CONFIRMED" | "PENDING". Abandoned carts are a separate
 * axis (`isAbandoned`) and are left to the caller.
 */

// `CANCELED` is the legacy US spelling, still present in older documents.
const CANCELLED_STATUSES = ["CANCELLED", "CANCELED"];
const CHARGED_PAYMENT_STATUSES = ["CHARGED", "ADDED_TO_MONTHLY_TOTAL"];

export const EFFECTIVE_STATUS_LABELS = {
    CONFIRMED: "Confirmed",
    CANCELLED: "Cancelled",
    PENDING: "Pending Confirmation",
};

// v2/v3 bookings have their own detail screen; everything older keeps the
// original edit page.
export const getBookingDetailPath = (booking) =>
    booking?.dataVersion === "v2" || booking?.dataVersion === "v3"
        ? `/admin/bookinglist/edit/v2/${booking?._id}`
        : `/admin/bookinglist/edit/${booking?._id}`;

export const getEffectiveBookingStatus = (booking) => {
    const status = String(booking?.bookingStatus || "").toUpperCase();
    const paymentStatus = String(booking?.paymentStatus || "").toUpperCase();

    if (CANCELLED_STATUSES.includes(status)) return "CANCELLED";
    if (status === "CONFIRMED" && CHARGED_PAYMENT_STATUSES.includes(paymentStatus)) return "CONFIRMED";
    return "PENDING";
};

// Where the booking list's date filter starts when nothing has been saved yet, and
// where it returns when the filters are cleared.
//
// Built fresh on each call rather than held as a constant, so a tab left open overnight
// still resolves "today" to the current day.
//
// Day boundaries mirror what the date picker emits (00:00:00 → 23:59:59): the backend
// compares pickupDateTime with exact $gte/$lte datetimes, so starting at the current
// time would silently hide bookings from earlier today.
export const getDefaultBookingDateRange = () => ({
    startDate: moment().startOf("day"),
    endDate: moment().add(1, "year").endOf("day"),
});

const BOOKING_DATE_RANGE_KEY = "admin.bookingList.dateRange";

// The last range the admin picked is remembered across visits (and browser restarts),
// so reopening the booking list lands on the window they were last working in instead
// of snapping back to today → +1 year.
export const saveBookingDateRange = (dateRange) => {
    const start = moment(dateRange?.startDate);
    const end = moment(dateRange?.endDate);

    if (!start.isValid() || !end.isValid()) return;

    try {
        localStorage.setItem(
            BOOKING_DATE_RANGE_KEY,
            JSON.stringify({ startDate: start.toISOString(), endDate: end.toISOString() }),
        );
    } catch (error) {
        // Storage can be unavailable (private mode / quota). Persisting the filter is a
        // convenience, so fall through and keep the range in component state only.
    }
};

// Clearing the filters forgets the saved range entirely rather than saving today's
// default — otherwise that snapshot would go stale and the list would keep opening on a
// window that starts in the past.
export const clearStoredBookingDateRange = () => {
    try {
        localStorage.removeItem(BOOKING_DATE_RANGE_KEY);
    } catch (error) {
        // See saveBookingDateRange — storage being unavailable is not worth failing over.
    }
};

// Returns moment objects, matching what the date picker hands back through its callback,
// so the rest of the list never has to care whether the range was restored or defaulted.
export const getStoredBookingDateRange = () => {
    let stored;

    try {
        stored = localStorage.getItem(BOOKING_DATE_RANGE_KEY);
    } catch (error) {
        return null;
    }

    if (!stored) return null;

    try {
        const { startDate, endDate } = JSON.parse(stored);
        const start = moment(startDate);
        const end = moment(endDate);

        if (!start.isValid() || !end.isValid()) return null;

        return { startDate: start, endDate: end };
    } catch (error) {
        return null;
    }
};

export const getInitialBookingDateRange = () =>
    getStoredBookingDateRange() || getDefaultBookingDateRange();
